/**
 * SCM8 — bidirectional document ↔ BoM-item linkage.
 *
 * Pure helpers for keeping `document.linkedBomItemIds` and
 * `Quote.documentId` in sync when a vendor quote PDF is linked /
 * unlinked against one or more BoM items.
 *
 * Discipline: every mutation goes through these helpers so the
 * Quotes tab + the document drawer always read the same truth.
 * Callers pass *immutable* snapshots and receive new objects — no
 * in-place mutation.
 */
import type { SiteDocument } from '../../types/siteRag';
import type { Quote, SupplyChainPlan } from '../../types/supplyChain';

/**
 * Link a document to one or more BoM items, adding the IDs to
 * `document.linkedBomItemIds` (deduped). Returns a new document.
 */
export function linkDocumentToBomItems(
  document: SiteDocument,
  bomItemIds: string[],
): SiteDocument {
  if (bomItemIds.length === 0) return document;
  const existing = document.linkedBomItemIds ?? [];
  const merged = Array.from(new Set([...existing, ...bomItemIds]));
  // Avoid producing a new object when the merge is a no-op so React
  // memoised consumers don't re-render unnecessarily.
  if (merged.length === existing.length && merged.every((id, i) => id === existing[i])) {
    return document;
  }
  return { ...document, linkedBomItemIds: merged };
}

/**
 * Unlink a document from a BoM item, removing the ID from
 * `document.linkedBomItemIds`. Returns a new document.
 */
export function unlinkDocumentFromBomItem(
  document: SiteDocument,
  bomItemId: string,
): SiteDocument {
  const existing = document.linkedBomItemIds ?? [];
  if (!existing.includes(bomItemId)) return document;
  return {
    ...document,
    linkedBomItemIds: existing.filter((id) => id !== bomItemId),
  };
}

/**
 * Apply a one-to-many quote → BoM linkage to a SupplyChainPlan
 * snapshot. Returns a new plan with the quotes appended (one per
 * BoM item) along with the updated document.
 *
 * Use case: user uploads a multi-item quote PDF and multi-selects
 * three BoM items. The platform creates three Quote records (one per
 * line item, deduplicated by bomItemId) all back-pointing to the
 * same documentId. The single document holds the union of BoM item
 * IDs in `linkedBomItemIds`.
 *
 * Caller is responsible for assigning unique `quote.id` values via
 * the `quoteIdForItem` callback (typically `crypto.randomUUID()` or
 * a deterministic test fixture).
 */
export function linkQuoteAcrossBomItems(
  plan: SupplyChainPlan,
  quoteTemplate: Omit<Quote, 'id' | 'bomItemId'>,
  bomItemIds: string[],
  quoteIdForItem: (bomItemId: string) => string,
): { plan: SupplyChainPlan; newQuotes: Quote[] } {
  const newQuotes: Quote[] = bomItemIds.map((bomItemId) => ({
    ...quoteTemplate,
    id: quoteIdForItem(bomItemId),
    bomItemId,
  }));
  return {
    plan: { ...plan, quotes: [...plan.quotes, ...newQuotes] },
    newQuotes,
  };
}

/**
 * Reverse lookup — given a documentId, return all BoM items the
 * document is linked to (via its `linkedBomItemIds`). Returns an
 * empty array when the document is unknown or has no links.
 */
export function bomItemIdsForDocument(
  documents: SiteDocument[],
  documentId: string,
): string[] {
  const doc = documents.find((d) => d.id === documentId);
  return doc?.linkedBomItemIds ?? [];
}
